import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { USERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faBadgeCheck, faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';

import * as S from './SearchByPhoneNumberForm.styled';

const defaultColSpan = {
	md: 6
};

const SearchByPhoneNumber = NiceModal.create(() => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const modal = useModal();

	const [userData, setUserData] = useState(null);

	const schema = useMemo(
		() =>
			yup.object().shape({
				phoneNumber: yup.string().nullable().phoneNumber()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			phoneNumber: null
		}
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (formData) => {
		try {
			setUserData(null);
			const { data, status } = await axiosPrivate.post(USERS.GET_USER_BY_PHONE, {
				phoneNumber: formData.phoneNumber
			});
			if (status === 200 && data) {
				setUserData({ ...data, phoneNumber: formData.phoneNumber });
			} else {
				throw new Error(data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const handleCloseModal = () => {
		modal.remove();
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-search-phone">
			<ModalTitle>
				<Typography variant="p1">SearchByPhoneNumber</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="phoneNumber" isLoading={isLoading}>
						<StringInput name="phoneNumber" />
					</FormItem>
				</Form>

				{userData && (
					<UserDetails
						phoneNumber={userData?.phoneNumber}
						clientName={userData?.clientName}
						companyId={userData?.companyId}
						userId={userData?.userId}
						userEmail={userData?.userEmail}
						mobileVerified={userData?.mobileVerified}
						closeUserDetail={() => setUserData(null)}
					/>
				)}
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					label={'search'}
					disabled={!isDirty}
					icon={faSearch}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

const UserDetails = ({ phoneNumber, clientName, companyId, userId, userEmail, mobileVerified, closeUserDetail }) => (
	<S.UserDetailContainer>
		<S.TitleRow>
			<S.Row className="title">
				<Typography variant="p1">userDetails</Typography>
				<Typography variant="p1">({phoneNumber})</Typography>
			</S.Row>
			<Button icon={faXmark} onClick={closeUserDetail} variant="transparent" size="x-small" />
		</S.TitleRow>
		<S.UserDetails>
			<FormItem label="companyId" colSpan={defaultColSpan}>
				<Typography noWrap={true} color="var(--content-4)" autoTranslation={false}>
					{companyId}
				</Typography>
			</FormItem>
			<FormItem label="clientName" colSpan={defaultColSpan}>
				<Typography noWrap={true} color="var(--content-4)" autoTranslation={false}>
					{clientName}
				</Typography>
			</FormItem>
			<FormItem label="userId" colSpan={defaultColSpan}>
				<Typography noWrap={true} color="var(--content-4)" autoTranslation={false}>
					{userId}
				</Typography>
			</FormItem>
			<FormItem label="userEmail" colSpan={defaultColSpan}>
				<Typography noWrap={true} color="var(--content-4)" autoTranslation={false}>
					{userEmail}
				</Typography>
			</FormItem>
			<FormItem label="mobileVerified" colSpan={defaultColSpan}>
				<S.Row>
					<Typography noWrap={true} color="var(--content-4)">
						{mobileVerified ? 'otp.phoneVerified' : 'otp.phoneNotVerified'}
					</Typography>
					<Icon
						icon={faBadgeCheck}
						color={mobileVerified ? 'var(--blue)' : 'var(--content-4)'}
						fontSize={'16px'}
					/>
				</S.Row>
			</FormItem>
		</S.UserDetails>
	</S.UserDetailContainer>
);

export default SearchByPhoneNumber;
